import { z } from 'zod';
import { LocaleCode } from './localization.ts';

/**
 * Test send and preview links of the composers (BB-59, CMP-009). A test send delivers draft or saved content to
 * content-test profiles right away and answers per recipient; a preview link shows the rendered message to colleagues
 * without sign-in for 7 days.
 */
export const TEST_SEND_MAX_RECIPIENTS = 50;
export const TEST_SEND_CHANNELS = ['push_android', 'push_ios', 'content_card', 'webhook'] as const;
export type TestSendChannel = (typeof TEST_SEND_CHANNELS)[number];
/** Channels a preview link may show: what a person sees (a webhook request is internal). */
export const PREVIEW_LINK_CHANNELS = ['push_android', 'push_ios', 'content_card'] as const;
export type PreviewLinkChannel = (typeof PREVIEW_LINK_CHANNELS)[number];

/** A saved message: the campaign's draft (else published) variant. */
export const MessageRef = z.strictObject({ campaignId: z.uuid(), variantApiId: z.string().max(64).optional() });
export type MessageRef = z.infer<typeof MessageRef>;

const Sample = z.record(z.string(), z.unknown());
/** Attribute values used for rendering only (`first_name`, a custom key or `custom.<key>`); the profile is never changed. */
export const AttributeOverrides = z.record(z.string().min(1).max(128), z.unknown()).refine((o) => Object.keys(o).length <= 50, { message: 'at most 50 overrides' });

const content = {
  draftContent: z.record(z.string(), z.unknown()).optional(),
  messageRef: MessageRef.optional(),
  attributeOverrides: AttributeOverrides.optional(),
  eventPropertiesSample: Sample.optional(),
  apiTriggerPropertiesSample: Sample.optional(),
  contextSample: Sample.optional(),
};
const oneSource = (b: { draftContent?: unknown; messageRef?: unknown }) => Boolean(b.draftContent) !== Boolean(b.messageRef);

export const TestSendBody = z
  .strictObject({
    channel: z.enum(TEST_SEND_CHANNELS),
    ...content,
    recipients: z
      .strictObject({
        testGroupIds: z.array(z.uuid()).max(TEST_SEND_MAX_RECIPIENTS).optional(),
        profileIds: z.array(z.uuid()).max(TEST_SEND_MAX_RECIPIENTS).optional(),
      })
      .refine((r) => (r.testGroupIds?.length ?? 0) + (r.profileIds?.length ?? 0) > 0, { message: 'choose a content test group or a profile' }),
  })
  .refine(oneSource, { message: 'send draftContent or messageRef', path: ['draftContent'] });
export type TestSendBody = z.infer<typeof TestSendBody>;

export const TEST_SEND_STATUSES = ['accepted', 'no_reachable_device', 'aborted', 'error'] as const;
export type TestSendStatus = (typeof TEST_SEND_STATUSES)[number];

export interface TestSendResult {
  profileId: string;
  /** The installation of a push result (one result per device). */
  deviceId?: string;
  status: TestSendStatus;
  /** Why: `accepted_by_provider`, `card_created`, `queued`, a provider or abort code, `no_push_token`, … */
  detail?: string;
}

export interface TestSendResponse {
  testSendId: string;
  channel: TestSendChannel;
  results: TestSendResult[];
}

/** Whose data the preview link renders: no profile (overrides only) or a content-test profile, never real customers. */
export const PreviewLinkProfile = z.discriminatedUnion('kind', [z.strictObject({ kind: z.literal('synthetic') }), z.strictObject({ kind: z.literal('test_profile'), profileId: z.uuid() })]);
export type PreviewLinkProfile = z.infer<typeof PreviewLinkProfile>;

export const CreatePreviewLinkBody = z
  .strictObject({
    channel: z.enum(PREVIEW_LINK_CHANNELS),
    ...content,
    profile: PreviewLinkProfile.default({ kind: 'synthetic' }),
    locale: LocaleCode.optional(),
  })
  .refine(oneSource, { message: 'send draftContent or messageRef', path: ['draftContent'] });
export type CreatePreviewLinkBody = z.infer<typeof CreatePreviewLinkBody>;

export const PreviewLinksQuery = z.object({ campaignId: z.uuid().optional() });

export interface PreviewLinkView {
  id: string;
  channel: PreviewLinkChannel;
  campaignId: string | null;
  profileKind: PreviewLinkProfile['kind'];
  createdBy: string | null;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
  state: 'active' | 'expired' | 'revoked';
}

/** Only the creation answer carries the link: the token is stored hashed and cannot be shown again. */
export interface CreatedPreviewLink extends PreviewLinkView {
  url: string;
}

/** `GET /public/v1/previews/:token`: the rendered message and nothing else (no profile, campaign or employee). */
export interface PublicPreview {
  channel: PreviewLinkChannel;
  locale: string;
  payload: unknown;
  aborted?: { reason: string; detail?: string };
  warnings: Array<{ code: string; detail?: string }>;
  expiresAt: string;
}
